import { httpsCallable } from 'firebase/functions';
import { inspectionAuth, inspectionFirebaseConfigured, inspectionFunctions, functionsBaseUrl } from './inspectionFirebase';

async function call(name, data = {}, unwrapData = true, options = {}) {
  if (!inspectionFirebaseConfigured || !inspectionFunctions) {
    throw new Error('Inspection-project Firebase is not configured.');
  }
  if (!inspectionAuth?.currentUser) {
    throw new Error('Sign in to the inspection project before using inspection services.');
  }
  const response = await httpsCallable(inspectionFunctions, name, options)(data);
  const payload = response.data;
  return unwrapData && payload && Object.prototype.hasOwnProperty.call(payload, 'data')
    ? payload.data
    : payload;
}

async function attachmentRequest(name, method, params, body) {
  const user = inspectionAuth?.currentUser;
  if (!user) throw new Error('Sign in to the inspection project before using inspection services.');
  const token = await user.getIdToken();
  const query = new URLSearchParams(params);
  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), 120000);
  let response;
  try {
    response = await fetch(`${functionsBaseUrl}/${name}?${query}`, {
      method,
      signal: controller.signal,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': body.type || 'application/octet-stream' } : {}),
      },
      ...(body ? { body } : {}),
    });
  } catch (error) {
    if (controller.signal.aborted) {
      throw new Error('The attachment request timed out. Photos remain saved on this device; retry the upload from Home.');
    }
    throw error;
  } finally {
    clearTimeout(timeout);
  }
  if (!response.ok) {
    const message = method === 'GET' ? `Attachment request failed (${response.status}).` : await response.text();
    const error = new Error(message || `Attachment request failed (${response.status}).`);
    error.status = response.status;
    throw error;
  }
  return response;
}

export const inspectionApi = {
  async connectSlack(orgId) {
    const user = inspectionAuth?.currentUser;
    if (!inspectionFirebaseConfigured || !user) {
      throw new Error('Sign in to Fleet before installing Slack.');
    }
    const response = await fetch('/api/slack/oauth', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Authorization: `Bearer ${await user.getIdToken()}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ orgId }),
    });
    if (!response.ok) throw new Error(await response.text() || 'Unable to start Slack installation.');
    return response.json();
  },
  getOrganizations: () => call('getMyOrganizations'),
  async inspectFleetInvitation(token) {
    if (!inspectionFirebaseConfigured || !inspectionFunctions) {
      throw new Error('Inspection-project Firebase is not configured.');
    }
    const response = await httpsCallable(inspectionFunctions, 'inspectFleetInvitation')({ token });
    return response.data.data;
  },
  createFleetInvitation: (input) => call('createFleetInvitation', input),
  async listFleetInvitations(orgId) {
    const items = [];
    let cursor;
    do {
      const page = await call('listFleetInvitations', { orgId, limit: 100, ...(cursor ? { cursor } : {}) });
      items.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);
    return items;
  },
  revokeFleetInvitation: (input) => call('revokeFleetInvitation', input),
  resendFleetInvitationEmail: (input) => call('resendFleetInvitationEmail', input),
  acceptFleetInvitation: (input) => call('acceptFleetInvitation', input),
  setFleetMemberAccessLevel: (input) => call('setFleetMemberAccessLevel', input),
  deleteOwnAccount: () => call('requestInspectionAccountDeletion', { confirmation: 'DELETE' }, true, { timeout: 540000 }),
  getAccountManagementCapabilities: () => call('getAccountManagementCapabilities'),
  removeFleetMember: (input) => call('removeFleetMember', input),
  listAccountAuditLogs: (orgId) => call('listAccountAuditLogs', { orgId }),
  deleteFleetGlobalAccount: (input) => call('deleteFleetGlobalAccount', input, true, { timeout: 540000 }),
  lookupVin: (orgId, vin) => call('lookupInspectionVin', { orgId, vin }),
  recognizeVinCrop: (orgId, imageBase64) => call('recognizeInspectionVinCrop', { orgId, imageBase64 }, true, { timeout: 20_000 }),
  async listDrivers(orgId) {
    const items = [];
    let cursor;
    do {
      const page = await call('listFleetDrivers', { orgId, limit: 100, ...(cursor ? { cursor } : {}) });
      items.push(...page.items);
      cursor = page.nextCursor;
    } while (cursor);
    return items;
  },
  getRecordProfile: (orgId, kind, recordId, cursor = null) =>
    call('getFleetRecordProfile', { orgId, kind, recordId, ...(cursor ? { cursor } : {}) }),
  getFleetDashboard: (input) => call('getFleetDashboard', input),
  async listUnits(orgId, options = {}) {
    const units = [];
    let cursor;
    do {
      const page = await call('listUnits', { orgId, limit: 100, ...options, ...(cursor ? { cursor } : {}) });
      units.push(...(page.items || []));
      cursor = page.nextCursor;
    } while (cursor);
    return units;
  },
  getTemplates: (orgId, options = {}) => call('getTemplates', { orgId, ...options }, false),
  getInspectionTemplate: (orgId, templateVersionId) =>
    call('getInspectionTemplate', { orgId, templateVersionId }),
  createCustomTemplateDraft: (input) => call('createCustomTemplateDraft', input),
  adoptTemplateSuggestion: (input) => call('adoptTemplateSuggestion', input),
  updateCustomTemplateDraft: (input) => call('updateCustomTemplateDraft', input),
  publishCustomTemplate: (input) => call('publishCustomTemplate', input),
  archiveCustomTemplate: (input) => call('archiveCustomTemplate', input),
  removeTemplateSuggestion: (input) => call('removeTemplateSuggestion', input),
  listTemplateVersions: (orgId, templateId) => call('listTemplateVersions', { orgId, templateId }),
  restoreTemplateVersion: (input) => call('restoreTemplateVersion', input),
  async listFleetMembers(orgId) {
    const items = [];
    let cursor;
    do {
      const page = await call('listFleetMembers', {
        orgId,
        limit: 100,
        ...(cursor ? { cursor } : {}),
      });
      items.push(...(page.items || []));
      cursor = page.nextCursor;
    } while (cursor);
    return { items, nextCursor: null };
  },
  setFleetMemberStatus: (input) => call('setFleetMemberStatus', input),
  saveFleetUnit: (input) => call('saveFleetUnit', input),
  archiveFleetUnit: (input) => call('archiveFleetUnit', input),
  updateFleetOrganization: (input) => call('updateFleetOrganization', input),
  configureSlackDelivery: (input) => call('configureSlackDelivery', input),
  getSlackDeliveryStatus: (orgId, eventId) =>
    call('getSlackDeliveryStatus', { orgId, eventId }),
  reconcileSlackDelivery: (input) => call('reconcileSlackDelivery', input),
  recoverSlackInspectionDelivery: (input) => call('recoverSlackInspectionDelivery', input),
  createDraft: (input) => call('createInspectionDraft', input),
  getDraft: (orgId, draftId) => call('getInspectionDraft', { orgId, draftId }),
  updateDraft: (orgId, draftId, expectedRevision, patch) =>
    call('updateInspectionDraft', { orgId, draftId, expectedRevision, patch }),
  allocateAttachment: (input) => call('allocateAttachment', input),
  async uploadAttachment({ orgId, draftId, correctionId, attachmentId, blob }) {
    const parent = correctionId ? { correctionId } : { draftId };
    return attachmentRequest('uploadInspectionAttachment', 'POST', { orgId, ...parent, attachmentId }, blob);
  },
  finalizeAttachment: (orgId, draftId, attachmentId) =>
    call('finalizeAttachment', { orgId, draftId, attachmentId }),
  finalizeCorrectionAttachment: (orgId, correctionId, attachmentId) =>
    call('finalizeAttachment', { orgId, correctionId, attachmentId }),
  reportDefectCorrection: (input) => call('reportDefectCorrection', input),
  verifyDefectCorrection: (input) => call('verifyDefectCorrection', input),
  async downloadAttachment(orgId, attachmentId) {
    const response = await attachmentRequest('downloadInspectionAttachment', 'GET', { orgId, attachmentId });
    return response.blob();
  },
  submit: (orgId, draftId, expectedRevision, idempotencyKey) =>
    call('submitInspection', { orgId, draftId, expectedRevision, idempotencyKey }),
  listHistory: (orgId, filters = {}, cursor = null, limit = 25, options = {}) =>
    call('listInspectionHistory', {
      orgId, filters, limit,
      ...(cursor ? { cursor } : {}),
      ...(options.summaryOnly ? { summaryOnly: true } : {}),
    }),
  getInspection: (orgId, inspectionId, answerCursor = null) =>
    call('getInspection', { orgId, inspectionId, limit: 50, ...(answerCursor ? { answerCursor } : {}) }),
  eligibleExits: (orgId, unitIds) =>
    call('listEligibleExitInspections', { orgId, unitIds, limit: 50 }),
  listOutstandingDefects: (orgId, unitIds) =>
    call('listOutstandingEquipmentDefects', { orgId, unitIds }),
  appendAmendment: (orgId, inspectionId, reason, corrections) =>
    call('appendInspectionAmendment', { orgId, inspectionId, reason, corrections }),
  getUnitStatus: (orgId, unitId, currentOdometer) =>
    call('getUnitInspectionStatus', { orgId, unitId, currentOdometer }),
};

export default inspectionApi;
